import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

type EmptyStateVariant = 'page' | 'section' | 'quiet';

interface EmptyStateProps {
  /**
   * `page` (default): a card standing in for a list or table.
   * `section`: no card shell, for an empty list inside a card that already has one.
   * `quiet`: no card shell, centred, for an empty work queue that is a good outcome.
   */
  variant?: EmptyStateVariant;
  /** Decorative mark above the title; `quiet` only. */
  icon?: ReactNode;
  title: string;
  /** What the feature does for the user, in one sentence. */
  description?: ReactNode;
  primaryAction?: ReactNode;
  className?: string;
}

/**
 * A simple empty list: a title, an optional sentence and one action. Callers
 * decide WHEN it renders (feature gates, filters, loading); this component only
 * lays it out. A filtered list with no results is `NoFilterMatches` instead.
 *
 * A page's first-run state is `ChecklistEmptyState`, not this.
 */
export function EmptyState({ variant = 'page', icon, title, description, primaryAction, className }: EmptyStateProps) {
  const Heading = variant === 'page' ? 'h2' : 'h3';

  if (variant === 'quiet') {
    return (
      <div className={cn('flex flex-col items-center gap-1.5 px-4 py-8 text-center', className)}>
        {icon && <span aria-hidden="true">{icon}</span>}
        <Heading className="font-sans text-[15px] font-medium text-secondary">{title}</Heading>
        {description && <p className="text-sm text-text-muted">{description}</p>}
        {primaryAction && <div className="mt-1">{primaryAction}</div>}
      </div>
    );
  }

  return (
    <div
      className={cn(
        variant === 'page' && 'rounded-xl border border-border bg-bg-primary p-5 shadow-sm sm:p-8',
        variant === 'section' && 'py-4',
        className,
      )}
    >
      <div className="max-w-2xl">
        <Heading
          className={cn(
            'text-secondary',
            variant === 'page' ? 'font-serif text-xl font-semibold sm:text-2xl' : 'font-sans text-sm font-medium',
          )}
        >
          {title}
        </Heading>
        {description && <p className="mt-2 text-sm text-text-muted">{description}</p>}
      </div>

      {primaryAction && <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">{primaryAction}</div>}
    </div>
  );
}
